import React from 'react';
import PropTypes from 'prop-types';

import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import { get } from 'lodash-es';
import NZBankValidator from '@fnzc/nz-bank-account-validator';

import AccountBalanceOutlinedIcon from '@material-ui/icons/AccountBalanceOutlined';

import PageHeading from '../../../../components/PageHeading/PageHeading';
import IndentedCardHeader from '../../../../components/IndentedCardHeader/IndentedCardHeader';
import IndentedCardContent from '../../../../components/IndentedCardContent/IndentedCardContent';
import BaseDialog from '../../../../components/BaseDialog/BaseDialog.js';
import ReplaceContact from '../../../../components/Replace/ReplaceContact/ReplaceContact';
import BankDetailsCard from './BankDetailsCard/BankDetailsCard';
import BankDetailsDialog from './BankDetailsDialog/BankDetailsDialog';
import DeleteDialog from '../../../../components/DeleteDialog/DeleteDialog';
import ConfirmedDialog from '../../../../components/ConfirmedDialog/ConfirmedDialog';
import MessageCard from '../../../../components/MessageCard/MessageCard';
import ConfirmTenantDialog from '../../../../components/ConfirmTenantDialog/ConfirmTenantDialog';

import ReportProblemIcon from '@material-ui/icons/ReportProblemOutlined';
import InfoIcon from '@material-ui/icons/InfoOutlined';

import { withStyles } from '@material-ui/core/styles';
import styles from './styles.js';

import { getString } from '../../../../helpers/stringHelpers';

const stringsComponent = 'bankDetailsSection';

class BankDetailsSection extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      dialogOpen: false,
      deleteOpen: false,
      selectedAccountIndex: null,
      bankSameAccountErrorOpen: false,
      confirmTenantDialogOpen: false,
      multipleAccountsDialogOpen: false,
    };

    this.toggleDialog = this.toggleDialog.bind(this);
    this.toggleDeleteDialog = this.toggleDeleteDialog.bind(this);
    this.editBankAccount = this.editBankAccount.bind(this);
    this.createBankAccount = this.createBankAccount.bind(this);
    this.deleteBankAccount = this.deleteBankAccount.bind(this);
    this.toggleSameBankAccountErrorDialog =
      this.toggleSameBankAccountErrorDialog.bind(this);
    this.handleDeleteDialogSubmit = this.handleDeleteDialogSubmit.bind(this);
    this.handleConfirmed = this.handleConfirmed.bind(this);
    this.toggleConfirmTenantDialog = this.toggleConfirmTenantDialog.bind(this);
    this.handleConfirmTenantDialogSubmit =
      this.handleConfirmTenantDialogSubmit.bind(this);
    this.toggleMultipleAccountsDialog =
      this.toggleMultipleAccountsDialog.bind(this);
  }

  componentWillMount() {
    const { bankDetails } = this.props;

    if (bankDetails.loaded === 'LOADED') {
      // This is to keep track of whether the user has entered the same details twice
      // If they have entered the same details twice, show a error dialog
      const bankAccountUpdatedTime = localStorage.getItem(
        'bankAccountUpdatedTime'
      );
      const now = Math.floor(Date.now() / 1000);

      const timeHasPassed =
        bankAccountUpdatedTime && now - bankAccountUpdatedTime >= 15;

      if (timeHasPassed) {
        const isBankAccountError = bankDetails.data.length === 0;
        if (isBankAccountError) {
          this.toggleSameBankAccountErrorDialog();
        } else {
          localStorage.removeItem('bankAccountUpdatedTime');
        }
      }

      if (bankDetails.data.length > 1) {
        this.toggleMultipleAccountsDialog();
      }
    }
  }

  isBankAccountInvalid(accountNumber) {
    let [bank, branch, account, suffix] = accountNumber.split('-');
    return !NZBankValidator.isValidNZBankNumber(bank, branch, account, suffix);
  }

  toggleMultipleAccountsDialog() {
    this.setState({
      multipleAccountsDialogOpen: !this.state.multipleAccountsDialogOpen,
    });
  }

  handleConfirmTenantDialogSubmit() {
    this.toggleConfirmTenantDialog();
    this.toggleDialog();
  }

  toggleSameBankAccountErrorDialog() {
    const { bankSameAccountErrorOpen } = this.state;
    if (bankSameAccountErrorOpen) {
      localStorage.removeItem('bankAccountUpdatedTime');
    }
    this.setState({
      bankSameAccountErrorOpen: !bankSameAccountErrorOpen,
    });
  }

  toggleConfirmTenantDialog() {
    const { confirmTenantDialogOpen } = this.state;
    this.setState({
      confirmTenantDialogOpen: !confirmTenantDialogOpen,
    });
  }

  toggleDialog() {
    this.setState({
      dialogOpen: !this.state.dialogOpen,
    });
  }

  toggleDeleteDialog() {
    this.setState({
      deleteOpen: !this.state.deleteOpen,
    });
  }

  deleteBankAccount(index) {
    this.setState(
      {
        selectedAccountIndex: index,
      },
      () => this.toggleDeleteDialog()
    );
  }

  editBankAccount(index) {
    this.setState(
      {
        selectedAccountIndex: index,
      },
      () => this.toggleConfirmTenantDialog()
    );
  }

  createBankAccount() {
    this.setState(
      {
        selectedAccountIndex: null,
      },
      () => this.toggleConfirmTenantDialog()
    );
  }

  handleDeleteDialogSubmit() {
    const { bankDetails, deleteBankAccount } = this.props;
    const { selectedAccountIndex } = this.state;

    deleteBankAccount(bankDetails.data[selectedAccountIndex].accountId);
  }

  handleConfirmed() {
    const { setConfirmedOpen } = this.props;
    setConfirmedOpen();
  }

  render() {
    const {
      classes,
      bankDetails,
      updateBankAccount,
      createBankAccount,
      customerName,
      confirmedOpen,
      contactData,
      toggleContactModal,
      tenancyData,
      canEdit,
    } = this.props;
    const {
      dialogOpen,
      bankSameAccountErrorOpen,
      selectedAccountIndex,
      deleteOpen,
      confirmTenantDialogOpen,
      multipleAccountsDialogOpen,
    } = this.state;

    const bankDetailsData = bankDetails.data;

    const initialDialogData =
      selectedAccountIndex !== null
        ? {
            accountId: bankDetailsData[selectedAccountIndex].accountId,
            accountName: bankDetailsData[selectedAccountIndex].accountName,
            accountNumber: bankDetailsData[selectedAccountIndex].accountNumber,
            bankName: bankDetailsData[selectedAccountIndex].bankName,
            createdDate: bankDetailsData[selectedAccountIndex].createdDate,
          }
        : {
            accountId: '',
            accountName: '',
            accountNumber: '',
            bankName: '',
            createdDate: '',
          };

    const hasEmail = !!get(contactData, 'email', '');

    const bankDetailsRefreshRequired =
      bankDetails.refreshRequired ||
      !!localStorage.getItem('bankAccountUpdatedTime');

    return (
      <div className={classes.root}>
        {!hasEmail || bankDetailsData.length === 0 ? (
          <React.Fragment>
            <MessageCard icon={InfoIcon}>
              <ReplaceContact
                content={getString('bankDetailsSection', 'BANK_INFO_HELP')}
                action={toggleContactModal}
                isLink={true}
              />
            </MessageCard>
            <br />
          </React.Fragment>
        ) : null}
        {bankDetailsData && bankDetailsData.length > 0 ? (
          bankDetailsData.map((data, index) => (
            <React.Fragment>
              {index == 1 ? <br /> : null}
              <Card
                className={
                  this.isBankAccountInvalid(data.accountNumber)
                    ? classes.invalidAccount
                    : null
                }
              >
                <IndentedCardContent
                  body={
                    <div>
                      <BankDetailsCard
                        isInvalid={this.isBankAccountInvalid(
                          data.accountNumber
                        )}
                        accountName={data.accountName}
                        bankName={data.bankName}
                        accountNumber={data.accountNumber}
                        createdDate={data.createdDate}
                        refreshRequired={bankDetailsRefreshRequired}
                        editAction={() => this.editBankAccount(index)}
                        removeAction={() => this.deleteBankAccount(index)}
                        createAction={null}
                        primary={index === 0}
                        hasEmail={hasEmail}
                        key={index}
                        canEdit={canEdit}
                      />
                    </div>
                  }
                  noIndent={true}
                />
              </Card>
            </React.Fragment>
          ))
        ) : (
          <Card>
            <IndentedCardContent
              body={
                <div>
                  <BankDetailsCard
                    accountName=""
                    bankName=""
                    accountNumber=""
                    createdDate=""
                    refreshRequired={bankDetailsRefreshRequired}
                    editAction={null}
                    removeAction={null}
                    createAction={() => this.createBankAccount()}
                    primary={true}
                    hasEmail={hasEmail}
                    canEdit={canEdit}
                  />
                </div>
              }
              noIndent={true}
            />
          </Card>
        )}
        {multipleAccountsDialogOpen && (
          <BaseDialog
            open={multipleAccountsDialogOpen}
            content={
              <React.Fragment>
                <ReplaceContact
                  content={getString(
                    'bankDetailsSection',
                    'MULTIPLE_ACCOUNTS_POPUP_HELP'
                  )}
                  action={toggleContactModal}
                  isLink={true}
                />
              </React.Fragment>
            }
            buttonAction={this.toggleMultipleAccountsDialog}
            buttonLabel="Close"
            icon={ReportProblemIcon}
          />
        )}
        {dialogOpen && (
          <BankDetailsDialog
            open={dialogOpen}
            cancelAction={() => this.toggleDialog()}
            customerName={customerName}
            onError={() => console.log('error')}
            failureText={'error'}
            data={{ ...initialDialogData }}
            updateBankAccount={updateBankAccount}
            createBankAccount={createBankAccount}
            tenancyRef={tenancyData.tenancyReference}
          />
        )}
        {bankSameAccountErrorOpen && (
          <BaseDialog
            open={bankSameAccountErrorOpen}
            content={
              <ReplaceContact
                content={getString('bankDetailsSection', 'SAME_ACCOUNT_ERROR')}
                action={() => {}}
                isLink={true}
              />
            }
            buttonAction={this.toggleSameBankAccountErrorDialog}
            buttonLabel="Close"
            icon={ReportProblemIcon}
          />
        )}
        {deleteOpen && (
          <DeleteDialog
            open={deleteOpen}
            cancel={this.toggleDeleteDialog}
            deleteContact={this.handleDeleteDialogSubmit}
            subject={bankDetailsData[selectedAccountIndex].accountNumber}
            dialogContent={getString('bankDetails', 'DELETE_ACCOUNT_MESSAGE')}
          />
        )}
        {confirmedOpen && (
          <ConfirmedDialog
            title={getString('bankDetails', 'CONFIRMED_TITLE')}
            open={confirmedOpen}
            close={this.handleConfirmed}
          />
        )}
        {confirmTenantDialogOpen && (
          <ConfirmTenantDialog
            show={confirmTenantDialogOpen}
            ok={() => {
              this.handleConfirmTenantDialogSubmit();
            }}
            cancel={() => {
              this.toggleConfirmTenantDialog();
            }}
            toggleContactModal={toggleContactModal}
          />
        )}
      </div>
    );
  }
}

BankDetailsSection.propTypes = {
  classes: PropTypes.object.isRequired,
  canEdit: PropTypes.bool.isRequired,
};

BankDetailsSection.defaultProps = {};

export default withStyles(styles)(BankDetailsSection);
